import { useMemo } from 'react';
import { useLocation, useMatches } from 'react-router-dom';

import { appRoutes } from '@/constants/routes';

export type RouteInfo = {
  routeKey: string;
  basePath: string;
  isHome: boolean;
  isTags: boolean;
  isCortex: boolean;
  isLibrary: boolean;
  isInstructions: boolean;
  isSettings: boolean;
  isArchive: boolean;
  leftSidebar?: SidebarConfig;
  rightSidebar?: SidebarConfig;
};

type SidebarConfig = {
  visible: boolean;
  resizable: boolean;
  collapsible?: boolean;
};

type RouteHandle = {
  leftSidebar?: SidebarConfig;
  rightSidebar?: SidebarConfig;
};

const routeMap: Record<string, string> = {
  [appRoutes.tags]: 'tags',
  [appRoutes.home]: 'library',
  [appRoutes.cortex]: 'cortex',
  [appRoutes.library]: 'library',
  [appRoutes.instructions]: 'instructions',
  [appRoutes.archive]: 'archive',
  [appRoutes.settings]: 'settings',
};

export const useRouteInfo = (): RouteInfo => {
  const location = useLocation();
  const matches = useMatches();

  const basePath = useMemo(() => {
    const raw = location.hash?.startsWith('#') ? location.hash.slice(1) : location.pathname;

    const pathOnly = raw.split(/[?#]/)[0];

    const matchedBase = Object.values(appRoutes)
      .filter((route) => route !== '/')
      .find((route) => pathOnly === route || pathOnly.startsWith(`${route}/`));

    return matchedBase ?? appRoutes.home;
  }, [location.pathname]);

  const routeHandle = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const handle = matches[i]?.handle as RouteHandle | undefined;
      if (handle?.leftSidebar || handle?.rightSidebar) {
        return handle;
      }
    }
    return undefined;
  }, [matches]);

  return {
    basePath,
    routeKey: routeMap[basePath] ?? 'library',
    leftSidebar: routeHandle?.leftSidebar,
    rightSidebar: routeHandle?.rightSidebar,
    isHome: basePath === appRoutes.home,
    isTags: basePath === appRoutes.tags,
    isCortex: basePath === appRoutes.cortex,
    isLibrary: basePath === appRoutes.library,
    isInstructions: basePath === appRoutes.instructions,
    isSettings: basePath === appRoutes.settings,
    isArchive: basePath === appRoutes.archive,
  };
};
